import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';
import {
  getAccessToken,
  getCurrentSession,
  getEmailFromSession,
  getGroupsFromSession,
  signOut as cognitoSignOut,
} from './cognito';
import { appEnv } from '../config';
import { setAccessToken } from '../api/client';
import type { Role } from '../types';

interface AuthState {
  loading: boolean;
  session: CognitoUserSession | null;
  email?: string;
  roles: Role[];
}

interface AuthContextValue extends AuthState {
  isAdmin: boolean;
  isAuthenticated: boolean;
  accessToken?: string;
  refresh: () => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Wraps the whole app. Holds the current Cognito session in memory (never
 * in localStorage beyond what amazon-cognito-identity-js itself persists in
 * its own storage), exposes the caller's role from the verified ID token,
 * and force-signs-out after a period of inactivity per the project's
 * "sign-out on inactivity in the UI" requirement.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ loading: true, session: null, roles: [] });
  const inactivityTimer = useRef<ReturnType<typeof setTimeout>>();

  const refresh = useCallback(async () => {
    const session = await getCurrentSession();
    if (!session) {
      setAccessToken(undefined);
      setState({ loading: false, session: null, roles: [] });
      return;
    }
    setAccessToken(getAccessToken(session));
    setState({
      loading: false,
      session,
      email: getEmailFromSession(session),
      roles: getGroupsFromSession(session).filter((g): g is Role => g === 'Admin' || g === 'ReadOnly'),
    });
  }, []);

  const signOut = useCallback(() => {
    cognitoSignOut();
    setAccessToken(undefined);
    setState({ loading: false, session: null, roles: [] });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!state.session) return undefined;

    const resetTimer = () => {
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        signOut();
      }, appEnv.inactivityTimeoutMinutes * 60_000);
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((evt) => window.addEventListener(evt, resetTimer));
    resetTimer();

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, resetTimer));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    };
  }, [state.session, signOut]);

  const value: AuthContextValue = {
    ...state,
    isAuthenticated: !!state.session,
    isAdmin: state.roles.includes('Admin'),
    accessToken: state.session ? getAccessToken(state.session) : undefined,
    refresh,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider.');
  return ctx;
}
